// ── Stokvel receipts — client-side prep for the paid audit-trail add-on ──
// Photos of receipts are downscaled + JPEG-compressed before upload (egress is the
// only cost that scales, so smaller files = cheaper + faster for members). PDFs are
// uploaded as-is. A small thumbnail is made for images so list views don't fetch the
// full file. All of this runs in the browser; nothing is stored until the caller
// uploads the returned blobs.

import { uploadStokvelReceipt, deleteStokvelReceipt } from './firebase';

const MAX_FILE_MB = 10;
const IMAGE_MAX_DIM = 1600;   // longest edge of the stored image
const THUMB_MAX_DIM = 320;    // longest edge of the list thumbnail
const IMAGE_QUALITY = 0.7;

export function isImage(file) { return /^image\//.test(file && file.type); }
export function isPdf(file) { return (file && file.type) === 'application/pdf' || /\.pdf$/i.test(file && file.name || ''); }

function sanitize(name) {
  return String(name || 'receipt').replace(/[^a-zA-Z0-9._-]/g, '_').slice(-60);
}

// A collision-resistant storage key. Uses time + randomness so two uploads in the
// same second don't clash. (Browser code — Date/Math.random are available here.)
function storageKey(name) {
  const stamp = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${stamp}_${rand}_${sanitize(name)}`;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read the image.')); };
    img.src = url;
  });
}

function scaleToBlob(img, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * ratio));
    const h = Math.max(1, Math.round(img.height * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Image compression failed.')), 'image/jpeg', quality);
  });
}

// Validate, compress, and UPLOAD a receipt for a stokvel. Returns the metadata to
// store on the expense/income record (visible to all members). Throws on bad input.
export async function uploadReceipt(stokvelId, file) {
  if (!file) throw new Error('No file selected.');
  if (file.size > MAX_FILE_MB * 1024 * 1024) throw new Error(`File is larger than ${MAX_FILE_MB} MB. Please use a smaller scan or photo.`);

  const base = { name: file.name || 'receipt', uploadedAt: Date.now() };

  if (isPdf(file)) {
    const key = storageKey(file.name || 'invoice.pdf');
    const { path, url } = await uploadStokvelReceipt(stokvelId, key, file, 'application/pdf');
    return { ...base, path, url, thumbUrl: null, contentType: 'application/pdf', size: file.size, kind: 'pdf' };
  }

  if (isImage(file)) {
    const img = await loadImage(file);
    const [mainBlob, thumbBlob] = await Promise.all([
      scaleToBlob(img, IMAGE_MAX_DIM, IMAGE_QUALITY),
      scaleToBlob(img, THUMB_MAX_DIM, 0.6),
    ]);
    const key = storageKey((file.name || 'receipt').replace(/\.[^.]+$/, '') + '.jpg');
    const thumbKey = 'thumb_' + key;
    const [main, thumb] = await Promise.all([
      uploadStokvelReceipt(stokvelId, key, mainBlob, 'image/jpeg'),
      uploadStokvelReceipt(stokvelId, thumbKey, thumbBlob, 'image/jpeg'),
    ]);
    return { ...base, path: main.path, url: main.url, thumbPath: thumb.path, thumbUrl: thumb.url, contentType: 'image/jpeg', size: mainBlob.size, kind: 'image' };
  }

  throw new Error('Unsupported file. Attach a PDF invoice or a photo (JPG/PNG).');
}

// Upload MANY receipts (a build order can have several slips). Returns an array of
// metadata to store on the transaction's `receipts` field.
export async function uploadReceipts(stokvelId, files) {
  const list = Array.from(files || []).filter(Boolean);
  const out = [];
  for (const f of list) out.push(await uploadReceipt(stokvelId, f)); // sequential = gentler on mobile
  return out;
}

// Remove a receipt's stored file(s) — call when an expense/income row is deleted.
export async function removeReceipt(receipt) {
  if (!receipt) return;
  if (receipt.path) await deleteStokvelReceipt(receipt.path);
  if (receipt.thumbPath) await deleteStokvelReceipt(receipt.thumbPath);
}

// Remove all receipts on a transaction (single legacy `receipt` or new `receipts[]`).
export async function removeAllReceipts(txn) {
  const list = receiptsOf(txn);
  for (const r of list) { try { await removeReceipt(r); } catch (e) { /* best-effort */ } }
}

// Normalise a transaction's receipts to an array (supports the legacy single `receipt`).
export function receiptsOf(txn) {
  if (!txn) return [];
  if (Array.isArray(txn.receipts)) return txn.receipts.filter(Boolean);
  return txn.receipt ? [txn.receipt] : [];
}
